import React, { useCallback, useState } from 'react';
import { Col, Layout, Skeleton, Typography } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { HomeTilesModal, HomeTilesPageHeader, HomeTilesTable } from '../../organisms/home';
import { API_URLS } from '../../../constants/urls/api-urls';
import { HomeWidgetsRecord, getHomeWidgetsRepository } from '../../../repository/mytm/home/homeWidgetsRepository';
import { HomeGroupKey, HomeWidgetStatus } from '../../../types/home';
import { HOME } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';

const { Content } = Layout;

export const HorizontalTiles = () => {
  const { t } = useTranslation(HOME);
  const [loading] = useState(false);
  const [isOpenHomeTilesModal, setIsOpenHomeTilesModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<HomeWidgetsRecord>();
  const groupKeyHomeTiles = HomeGroupKey.HOME_TILES;
  const { data, isValidating, error, mutate } = useSWR(
    [API_URLS.HOME_WIDGETS, groupKeyHomeTiles],
    (url: string, groupKey) => getHomeWidgetsRepository({ groupKey }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onOpenHomeTilesModal = useCallback(() => {
    setIsOpenHomeTilesModal(true);
  }, []);

  const onCloseHomeTilesModal = useCallback(() => {
    setIsOpenHomeTilesModal(false);
    setCurrentRecord(undefined);
  }, []);

  const onHandleClickBtnEdit = useCallback((record: HomeWidgetsRecord) => {
    setCurrentRecord(record);
    setIsOpenHomeTilesModal(true);
  }, []);

  if (!data || error) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    );
  }

  const recordHomeTilesActive = data.filter(
    item => item.groupKey === HomeGroupKey.HOME_TILES && item.status === HomeWidgetStatus.ACTIVE,
  );

  const recordHomeTilesDraft = data
    .filter(item => item.groupKey === HomeGroupKey.HOME_TILES && item.status === HomeWidgetStatus.INACTIVE)
    .sort((a: any, b: any) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .map((item: any, index: number) => ({ ...item, ...{ index } }));

  const recordHomeTilesClosed = data
    .filter(item => item.groupKey === HomeGroupKey.HOME_TILES && item.status === HomeWidgetStatus.CLOSED)
    .sort((a: any, b: any) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  const recordHomeTiles = [...recordHomeTilesActive, ...recordHomeTilesClosed].map((item: any, index: number) => ({
    ...item,
    ...{ index },
  }));

  return (
    <>
      <Container>
        <Content>
          <HomeTilesPageHeader onOpenHomeTilesModal={onOpenHomeTilesModal} />
          <Col span={14}>
            <HomeTilesTable
              recordsActive={recordHomeTilesActive}
              records={recordHomeTiles}
              loading={loading || isValidating}
              isActiveList
              onRefresh={mutate}
              onHandleClickBtnEdit={onHandleClickBtnEdit}
            />
          </Col>
          {recordHomeTilesDraft.length > 0 && (
            <Col span={14} className="drafts-list">
              <Typography.Title level={4}>{t('draftsList')}</Typography.Title>
              <HomeTilesTable
                records={recordHomeTilesDraft}
                loading={loading || isValidating}
                isActiveList={false}
                onRefresh={mutate}
                onHandleClickBtnEdit={onHandleClickBtnEdit}
                recordsActive={recordHomeTilesActive}
              />
            </Col>
          )}
          <HomeTilesModal
            isOpenModal={isOpenHomeTilesModal}
            onClose={onCloseHomeTilesModal}
            onRefresh={mutate}
            record={currentRecord}
          />
        </Content>
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .drafts-list,
  & .closed-list {
    margin-top: 60px;
  }
`;
